import type { Editor, JSONContent } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";

// The nodes the pickers insert, as the serializer's schema holds them (chapter_markdown/index.ts): it alone writes the
// Markdown. `args` is the text between a tag's brackets.

// The size classes a map or a picture takes, in the reader's CSS (`img.large` and the others).
export const MAP_SIZES = [
	{ cls: "large", label: "size_large" },
	{ cls: "tall", label: "size_tall" },
	{ cls: "fullsize", label: "size_fullsize" },
	{ cls: "flat", label: "size_flat" },
];
export const PICTURE_SIZES = [
	...MAP_SIZES,
	{ cls: "medium", label: "size_medium" },
	{ cls: "small", label: "size_small" },
	{ cls: "minimap", label: "size_minimap" },
];

export const STAGES = ["1", "2", "3"];

// Tags the picker inserts as blocks and as inline text, and the picker kind each belongs to.
export const BOX_TAGS = { monster: "MONSTERBOX", spell: "SPELLBOX", character: "CHARACTERBOX", retainer: "RETAINERBOX" } as const;
export const LINK_TAGS = { monstro: "MONSTRO", magia: "MAGIA", equipamento: "EQUIPAMENTO" } as const;

export const box = (tag: string, args: string): JSONContent => ({ type: "odoBox", attrs: { tag, args } });
export const tag = (name: string, args: string): JSONContent => ({ type: "odoTag", attrs: { tag: name, args } });
export const monsterBox = (slug: string, format: string | null) => box(BOX_TAGS.monster, format ? `${slug}, ${format}` : slug);
export const stage = (level: string) => tag("ESTAGIO", level);

// `slug, format` of a MONSTERBOX; the format is null for the full card.
export function monsterArgs(args: string) {
	const [slug, format] = args.split(",").map((part) => part.trim());
	return { slug, format: format || null };
}

// Text a tag or a picture's attributes cannot carry is dropped, never escaped: the notation has no way to say it.
const plain = (text: string, drop: RegExp) => text.replace(drop, " ").replace(/\s+/g, " ").trim();

export const mapNode = (path: string, size: string, title: string) => box("MAPA", `${path}, ${size}, ${plain(title, /[\]\n\r]/g)}`);

// `path, size, title`: the title may hold commas.
export function mapParts(args: string) {
	const [path, size, ...title] = args.split(",");
	return { path: path.trim(), size: (size ?? "").trim(), title: title.join(",").trim() };
}

export const pictureNode = (src: string, title: string, size: string): JSONContent => {
	const alt = plain(title, /[[\]\n\r]/g);
	const caption = plain(title, /["[\]\n\r]/g);
	return { type: "odoImage", attrs: { src, alt, title: caption || null, gap: " ", ial: `{: class="${size}"}` } };
};

// A file name as a title to start from: "caverna-dos-kobolds.webp" is "Caverna dos kobolds".
export function humanize(file: string): string {
	const words = (file.split("/").pop() ?? "")
		.replace(/\.\w+$/, "")
		.replace(/[-_]+/g, " ")
		.trim();
	return words.charAt(0).toUpperCase() + words.slice(1);
}

// What Rails' `parameterize` makes of a name, for the path of a record the author named.
export const parameterize = (name: string) =>
	name
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");

// Points the selected text at `href`, or the link the caret is in; with no selection the label becomes the link text.
export function applyLink(editor: Editor, href: string, label: string) {
	const chain = editor.chain().focus();
	if (!editor.state.selection.empty) chain.setLink({ href }).run();
	else if (editor.isActive("link")) chain.extendMarkRange("link").setLink({ href }).run();
	else chain.insertContent({ type: "text", text: label, marks: [{ type: "link", attrs: { href } }] }).run();
}

// A header row and two rows of two cells, with a caption to fill in: the book's table with nothing in it yet.
export function newTable(t: (key: string) => string): JSONContent {
	const row = (section: string, cells: JSONContent[]): JSONContent => ({ type: "tableRow", attrs: { section }, content: cells });
	const header = (n: number): JSONContent => ({ type: "tableHeader", content: [{ type: "text", text: t("table_column").replace("%{n}", String(n)) }] });
	const body = (): JSONContent => row("tbody", [{ type: "tableCell" }, { type: "tableCell" }]);
	return {
		type: "table",
		attrs: { caption: { title: t("table_caption_default"), number: null } },
		content: [row("thead", [header(1), header(2)]), body(), body()],
	};
}

// A new block goes where the caret is when that line is empty, otherwise after the top-level block it is in.
// Returns where it went.
export function insertBlock(editor: Editor, node: JSONContent): number {
	const { selection } = editor.state;
	const { $from } = selection;
	const empty = $from.depth === 1 && $from.parent.type.name === "paragraph" && $from.parent.content.size === 0;
	const at = empty ? { from: $from.before(), to: $from.after() } : selection instanceof NodeSelection || $from.depth === 0 ? selection.to : $from.after(1);
	editor.chain().focus().insertContentAt(at, node).run();
	return typeof at === "number" ? at : at.from;
}

export const insertInline = (editor: Editor, node: JSONContent) => void editor.chain().focus().insertContent(node).run();
